/**
 * components/FileUpload.jsx
 * Professional two-pane upload card.
 * Left pane: CC symbol selector + drag-and-drop zone.
 * Right pane: scrollable file queue + prominent Start button.
 */

import React, { useState, useRef } from 'react';
import API from '../services/api';
import '../styles/components.css';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/tiff', 'application/pdf'];
const FORMAT_TAGS   = ['PNG', 'JPEG', 'TIFF', 'PDF'];
const MAX_FILES = 10;
/** Uploads run in parallel via Promise.all (one task per file index; max MAX_FILES at once). */

const STEPS = [
  { num: '1', label: 'Upload Drawings' },
  { num: '2', label: 'AI Analysis'     },
  { num: '3', label: 'Get Excel Plans' },
];

const CC_SYMBOL_OPTIONS = [
  { key: 'spade',               label: 'Spade (♠)' },
  { key: 'club',                label: 'Club (♣)' },
  { key: 'non_filled_square',   label: 'Non-Filled Square (□)' },
  { key: 'filled_square',       label: 'Filled Square (■)' },
  { key: 'non_filled_circle',   label: 'Non-Filled Circle (○)' },
  { key: 'circle_filled',       label: 'Filled Circle (●)' },
  { key: 'non_filled_triangle', label: 'Non-Filled Triangle (△)' },
  { key: 'filled_triangle',     label: 'Filled Triangle (▲)' },
  { key: 'non_filled_diamond',  label: 'Non-Filled Diamond (◇)' },
  { key: 'filled_diamond',      label: 'Filled Diamond (◆)' },
  { key: 'oblong',              label: 'Oblong (⬭)' },
];

function UploadCloudIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 16 12 12 8 16"/>
      <line x1="12" y1="12" x2="12" y2="21"/>
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
    </svg>
  );
}

function DocIcon({ name }) {
  const ext = (name.split('.').pop() || '').toUpperCase();
  const isImg = ['PNG', 'JPG', 'JPEG', 'TIFF', 'TIF'].includes(ext);
  return (
    <div className={`fu-file-icon ${isImg ? 'img' : 'pdf'}`}>
      {ext.slice(0, 3)}
    </div>
  );
}

function EmptyQueueIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.25 }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="13" x2="15" y2="13"/>
      <line x1="9" y1="17" x2="12" y2="17"/>
    </svg>
  );
}

export default function FileUpload({ onFilesUploaded, onTabularScan, onError }) {
  const [ccSymbols,    setCcSymbols]    = useState(['spade']);
  const [isDragging,   setIsDragging]   = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading,    setUploading]    = useState(false);
  const [batchName,    setBatchName]    = useState('');
  const fileInputRef = useRef(null);

  const handleDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = ()  => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) addFiles(Array.from(e.dataTransfer.files));
  };

  const handleInput = (e) => {
    if (e.target.files.length > 0) addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const addFiles = (newFiles) => {
    const valid = [];
    const errors = [];
    for (const f of newFiles) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        errors.push(`${f.name}: unsupported type (use PNG, JPEG, TIFF or PDF)`);
        continue;
      }
      if (f.size > 50 * 1024 * 1024) {
        errors.push(`${f.name}: too large (max 50 MB)`);
        continue;
      }
      valid.push(f);
    }
    if (errors.length) onError(errors.join('\n'));
    setPendingFiles(prev => {
      const combined = [...prev, ...valid.map(f => ({ file: f, progress: 0, status: 'queued', error: null }))];
      return combined.slice(0, MAX_FILES);
    });
  };

  const removeFile = (idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx));

  const startUploads = async () => {
    if (pendingFiles.length === 0) return;
    setUploading(true);
    const isBatch  = pendingFiles.length > 1;
    const batchId  = isBatch ? crypto.randomUUID() : null;
    // Use the trimmed user name if provided, otherwise null (backend shows "Batch")
    const resolvedBatchName = isBatch && batchName.trim() ? batchName.trim() : null;

    const queue = [...pendingFiles];
    const UPLOAD_ATTEMPTS = 3;

    // One async task per explicit index — dense results (no shared counter / sparse-array holes).
    // Each file retries up to UPLOAD_ATTEMPTS times with backoff before being marked failed,
    // so a transient server hiccup doesn't permanently drop a file from the batch.
    const results = await Promise.all(
      queue.map(async (pf, idx) => {
        setPendingFiles((prev) =>
          prev.map((item, i) => (i === idx ? { ...item, status: 'uploading' } : item))
        );

        let lastErr = null;
        for (let attempt = 0; attempt < UPLOAD_ATTEMPTS; attempt++) {
          try {
            const result = await API.upload(
              pf.file,
              (pct) => {
                setPendingFiles((prev) =>
                  prev.map((item, i) => (i === idx ? { ...item, progress: pct } : item))
                );
              },
              batchId,
              resolvedBatchName
            );
            setPendingFiles((prev) =>
              prev.map((item, i) =>
                i === idx ? { ...item, status: 'done', progress: 100 } : item
              )
            );
            return {
              ok: true,
              fileId: result.file_id,
              fileName: result.file_name,
              ccSymbols,
              batchId,
            };
          } catch (err) {
            lastErr = err;
            if (attempt < UPLOAD_ATTEMPTS - 1) {
              await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
            }
          }
        }

        // All attempts exhausted
        setPendingFiles((prev) =>
          prev.map((item, i) =>
            i === idx
              ? { ...item, status: 'error', error: lastErr?.error || 'Upload failed' }
              : item
          )
        );
        return { ok: false, fileName: pf.file.name };
      })
    );

    if (results.length !== queue.length) {
      onError('Upload finished with an unexpected state. Please try again.');
      setUploading(false);
      return;
    }

    const succeeded = results.filter((r) => r && r.ok);
    const failed = results.filter((r) => r && !r.ok);
    if (failed.length) onError(`${failed.length} file(s) failed to upload: ${failed.map(f => f.fileName).join(', ')}`);
    if (succeeded.length > 0) {
      onFilesUploaded(succeeded.map(r => ({ fileId: r.fileId, fileName: r.fileName, ccSymbols: r.ccSymbols, batchId: r.batchId })));
    } else {
      setUploading(false);
    }
  };

  const allDone    = pendingFiles.length > 0 && pendingFiles.every(f => f.status === 'done');
  const overallPct = pendingFiles.length === 0 ? 0
    : Math.round(pendingFiles.reduce((s, f) => s + f.progress, 0) / pendingFiles.length);
  const canStart   = pendingFiles.length > 0 && !uploading && !allDone;

  const statusLabel = { queued: 'Queued', uploading: 'Uploading…', done: 'Done', error: 'Failed' };

  return (
    <div className="file-upload-container">

      {/* Page header */}
      <div className="fu-page-header">
        <div className="upload-hero-eyebrow">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          AI-Powered · Up to {MAX_FILES} drawings at once
        </div>
        <h1>Analyse your engineering drawings</h1>
        <p>Upload drawings, get AI-generated inspection plans with critical characteristics.</p>
      </div>

      {/* Main card */}
      <div className="fu-card">

        {/* ── Left pane: configure + drop ── */}
        <div className="fu-pane fu-pane-left">
          <p className="fu-pane-label">Configure &amp; Drop</p>

          {/* CC Symbol multi-select grid */}
          <div className="fu-cc-section">
            <div className="fu-cc-section-header">
              <span className="fu-cc-section-label">CC Symbols</span>
              <span className="fu-cc-section-hint">
                {ccSymbols.length === 1 ? '1 selected' : `${ccSymbols.length} selected`}
              </span>
            </div>
            <div className="fu-cc-grid">
              {CC_SYMBOL_OPTIONS.map(opt => {
                const checked = ccSymbols.includes(opt.key);
                const toggle = () => {
                  if (uploading) return;
                  setCcSymbols(prev =>
                    checked
                      ? prev.length > 1 ? prev.filter(k => k !== opt.key) : prev
                      : [...prev, opt.key]
                  );
                };
                return (
                  <button
                    key={opt.key}
                    type="button"
                    className={`fu-cc-chip${checked ? ' selected' : ''}`}
                    onClick={toggle}
                    disabled={uploading}
                    title={opt.label}
                  >
                    <span className="fu-cc-chip-glyph">{opt.label.match(/\((.+)\)/)?.[1] ?? opt.label}</span>
                    <span className="fu-cc-chip-name">{opt.label.replace(/\s*\(.+\)/, '')}</span>
                    {checked && <span className="fu-cc-chip-check">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Drop zone */}
          <div
            className={`fu-dropzone${isDragging ? ' dragging' : ''}${uploading ? ' loading' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.tiff,.tif,.pdf"
              multiple
              onChange={handleInput}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            <div className="fu-drop-icon-wrap">
              <UploadCloudIcon />
            </div>
            <p className="fu-drop-title">{isDragging ? 'Release to add files' : 'Drop drawings here'}</p>
            <p className="fu-drop-sub">or click anywhere to browse</p>
            <button
              className="fu-browse-btn"
              onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
              disabled={uploading}
            >
              {uploading ? `Uploading… ${overallPct}%` : 'Browse Files'}
            </button>
            <div className="fu-formats">
              {FORMAT_TAGS.map(f => <span key={f} className="format-tag">{f}</span>)}
              <span className="fu-formats-note">· 50 MB max each</span>
            </div>
          </div>
        </div>

        {/* Vertical divider */}
        <div className="fu-divider" />

        {/* ── Right pane: file queue + CTA ── */}
        <div className="fu-pane fu-pane-right">

          {/* Queue header */}
          <div className="fu-queue-header">
            <p className="fu-pane-label" style={{ margin: 0 }}>Selected Files</p>
            {pendingFiles.length > 0 && (
              <span className="fu-count-badge">{pendingFiles.length} / {MAX_FILES}</span>
            )}
            {!uploading && pendingFiles.length > 0 && (
              <button className="fu-clear-btn" onClick={() => { setPendingFiles([]); setBatchName(''); }}>
                Clear all
              </button>
            )}
          </div>

          {/* Batch name input — shown only when 2+ files are queued */}
          {pendingFiles.length > 1 && (
            <div style={{
              margin: '0 0 10px',
              padding: '10px 12px',
              background: '#FFF7ED',
              border: '1px solid #FDE8C8',
              borderRadius: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
            }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#B45309', display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Batch Name <span style={{ color: '#9C9490', fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                type="text"
                value={batchName}
                onChange={e => setBatchName(e.target.value)}
                disabled={uploading}
                placeholder={`Batch — ${pendingFiles.length} files`}
                maxLength={100}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '7px 10px',
                  borderRadius: 7,
                  border: '1.5px solid #FDE8C8',
                  background: uploading ? '#FFF3E0' : '#fff',
                  fontSize: '0.85rem',
                  color: '#1A1410',
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
                onFocus={e  => { e.target.style.borderColor = '#F97316'; }}
                onBlur={e   => { e.target.style.borderColor = '#FDE8C8'; }}
              />
              <span style={{ fontSize: '0.7rem', color: '#9C9490' }}>
                Displayed as: <strong style={{ color: '#5C5450' }}>
                  {batchName.trim() ? `${batchName.trim()} (${pendingFiles.length} files)` : `Batch (${pendingFiles.length} files)`}
                </strong>
              </span>
            </div>
          )}

          {/* File list */}
          <div className="fu-file-list">
            {pendingFiles.length === 0 ? (
              <div className="fu-empty">
                <EmptyQueueIcon />
                <span className="fu-empty-title">No files added yet</span>
                <span className="fu-empty-sub">Drop or browse files on the left to get started</span>
              </div>
            ) : (
              pendingFiles.map((pf, idx) => (
                <div key={idx} className={`fu-file-row ${pf.status}`}>
                  <DocIcon name={pf.file.name} />
                  <div className="fu-file-info">
                    <span className="fu-file-name" title={pf.file.name}>{pf.file.name}</span>
                    <div className="fu-file-meta-row">
                      <span className="fu-file-size">{(pf.file.size / 1024 / 1024).toFixed(1)} MB</span>
                      <span className={`fu-status-chip ${pf.status}`}>
                        {pf.status === 'uploading' && (
                          <span className="fu-spin" />
                        )}
                        {statusLabel[pf.status]}
                      </span>
                    </div>
                    {pf.status === 'uploading' && (
                      <div className="fu-file-progress">
                        <div className="fu-file-progress-fill" style={{ width: `${pf.progress}%` }} />
                      </div>
                    )}
                    {pf.status === 'error' && (
                      <span className="fu-file-error">{pf.error}</span>
                    )}
                  </div>
                  {!uploading && (
                    <button className="fu-remove-btn" onClick={() => removeFile(idx)} title="Remove">
                      ×
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Smart Scan CTA — uploads all files then goes to scan-type review step */}
          <button className="fu-start-btn" onClick={startUploads} disabled={!canStart}>
            {uploading ? (
              <>
                <span className="fu-spin light" />
                Uploading… {overallPct}%
              </>
            ) : (
              <>
                Analyse Drawings
                {pendingFiles.length > 0 && (
                  <span className="fu-start-badge">{pendingFiles.length}</span>
                )}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </>
            )}
          </button>
          <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: 'var(--text-secondary, #6B7280)', textAlign: 'center' }}>
            AI will auto-detect Normal or Tabular scan for each drawing
          </p>

        </div>
      </div>

      {/* How it works */}
      <div className="upload-steps">
        {STEPS.map((step, i) => (
          <React.Fragment key={step.num}>
            <div className="upload-step">
              <div className="upload-step-num">{step.num}</div>
              <div className="upload-step-label">{step.label}</div>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: '14px' }}>
                <div style={{ width: '40px', height: '2px', background: 'var(--border-strong)' }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

    </div>
  );
}
